import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

interface ITimerProps {
  thickness?: number;
  precision?: number;
  progress?: number;
  startProgress?: number;
  updateEveryMilliseconds?: number;
  fontSize?: number;
  text?: string;
  secondaryText?: string;
  primaryColor?: string;
  secondaryColor?: string;
  style?: React.CSSProperties;
  radius?: number;
  stroke?: "butt" | "round" | "square" | "inherit";
  trackTooltip?: string;
  progressTooltip?: string;
  primaryTextTooltip?: string;
  secondaryTextTooltip?: string;
}

const SvgTooltip = ({ tooltip, children }: { tooltip?: string; children: React.ReactElement }) => {
  if (!tooltip) return children;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className="rounded bg-foreground px-2 py-1 text-xs text-background shadow-md"
          sideOffset={5}
        >
          {tooltip}
          <TooltipPrimitive.Arrow className="fill-foreground" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

const Timer: React.FC<ITimerProps> = ({
  thickness = 8,
  precision = 1000,
  progress = 0,
  startProgress = 0,
  updateEveryMilliseconds = 1000,
  fontSize = 16,
  text = "",
  secondaryText = "",
  primaryColor = "#304EA1",
  secondaryColor = "#D8D9DB",
  radius = 240,
  stroke = "round",
  style,
  trackTooltip,
  progressTooltip,
  primaryTextTooltip,
  secondaryTextTooltip,
}) => {
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;

    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians)),
    };
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    // Guard against invalid inputs producing NaN in path
    if (!Number.isFinite(radius) || !Number.isFinite(startAngle) || !Number.isFinite(endAngle)) {
      return "";
    }

    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);

    if (!Number.isFinite(start.x) || !Number.isFinite(start.y) || !Number.isFinite(end.x) || !Number.isFinite(end.y)) {
      return "";
    }

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    const d = [
      "M", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    ].join(" ");

    return d;
  };

  const calculateParams = () => {
    const viewboxHeight = precision;
    const viewboxWidth = precision;
    const calculatedThickness = thickness * precision / 100;
    const calculatedFontSize = fontSize * precision / 100;

    const arcRadius = radius;

    const centerX = viewboxWidth / 2;
    const centerY = viewboxHeight / 2;

    const emptyRadius = 360 - arcRadius;
    const halfEmptyRadius = emptyRadius / 2;
    const startAngle = halfEmptyRadius - 180;
    const endAngle = 180 - halfEmptyRadius;
    const clamp01 = (v: any) => (Number.isFinite(v) ? Math.min(Math.max(Number(v), 0), 1) : 0);

    // Sanitize progress values to avoid NaN
    const safeEndProgress = clamp01(progress);
    const safeStartProgress = clamp01(startProgress);

    const segmentStartProgress = Math.min(safeStartProgress, safeEndProgress);
    const segmentEndProgress = Math.max(safeStartProgress, safeEndProgress);

    const angleAt = (p: number) => (arcRadius * p) - 180 + halfEmptyRadius;
    const segmentStartAngle = angleAt(segmentStartProgress);
    const segmentEndAngle = angleAt(segmentEndProgress);

    const calculatedRadius = Math.max(0, Math.min(viewboxWidth, viewboxHeight) / 2 - calculatedThickness);

    return {
      arcPath: describeArc(centerX, centerY, calculatedRadius, startAngle, endAngle),
      secondaryArcPath: segmentEndProgress <= segmentStartProgress
        ? ""
        : describeArc(centerX, centerY, calculatedRadius, segmentStartAngle, segmentEndAngle),
      viewboxWidth,
      viewboxHeight,
      calculatedThickness,
      calculatedFontSize,
    };
  };

  const {
    arcPath,
    secondaryArcPath,
    viewboxWidth,
    viewboxHeight,
    calculatedThickness,
    calculatedFontSize,
  } = calculateParams();

  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <svg
        viewBox={`0 0 ${viewboxWidth} ${viewboxHeight}`}
        style={style}
        role="img"
        aria-label={text ? `Timer: ${text}` : "Timer"}
      >
        <SvgTooltip tooltip={trackTooltip}>
          <g>
            <path
              strokeLinecap={stroke}
              stroke={secondaryColor}
              fill="none"
              strokeWidth={calculatedThickness}
              d={arcPath}
              shapeRendering="geometricPrecision"
            />
          </g>
        </SvgTooltip>
        <SvgTooltip tooltip={progressTooltip}>
          <g>
            <path
              strokeLinecap={stroke}
              stroke={primaryColor}
              fill="none"
              strokeWidth={calculatedThickness}
              d={secondaryArcPath}
              shapeRendering="geometricPrecision"
            />
          </g>
        </SvgTooltip>
        <SvgTooltip tooltip={primaryTextTooltip}>
          <text x="50%" y="50%" dominantBaseline="middle" fontSize={calculatedFontSize}
            textAnchor="middle">
            {text}
          </text>
        </SvgTooltip>
        <SvgTooltip tooltip={secondaryTextTooltip}>
          <text x="50%" y="65%" dominantBaseline="middle" fontSize={calculatedFontSize * 0.4}
            textAnchor="middle">
            {secondaryText}
          </text>
        </SvgTooltip>
      </svg>
    </TooltipPrimitive.Provider>
  );
};

export default Timer;
